import {
    RouterProvider,
    createBrowserRouter,
    RouteObject,
} from 'react-router-dom';
import React, {Suspense, lazy} from 'react';
import RootLayout from './pages/base/Root';
import LoadingSpinner from './components/UI/LoadingSpinner';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// Lazy load page components
const HomePage = lazy(() => import('./pages/Home.tsx'));
const AboutPage = lazy(() => import('./pages/About'));
const PeoplePage = lazy(() => import('./pages/People'));
const Publications = lazy(() => import('./pages/Publications'));
const Blog = lazy(() => import('./pages/Blogs'));
const OurNews = lazy(() => import('./pages/News'));
const ReportsPolicies = lazy(() => import('./pages/ReportsPolicies'));
const GalleryMedia = lazy(() => import('./pages/GalleryMedia'));

// Common wrapper for lazy loaded routes
const LazyLoadRoute = (Component: React.ComponentType<any>) => (
    <Suspense fallback={<LoadingSpinner />}>
        <Component />
    </Suspense>
);

const routes: RouteObject[] = [
    {
        path: '/',
        element: <RootLayout />,
        children: [
            {
                index: true,
                element: LazyLoadRoute(HomePage),
            },
            {
                path: '/:modalTitle',
                element: LazyLoadRoute(HomePage),
            },
            {
                path: '/about-project',
                element: LazyLoadRoute(AboutPage),
            },
            {
                path: '/team',
                element: LazyLoadRoute(PeoplePage),
            },
            {
                path: '/papers',
                element: LazyLoadRoute(Publications),
            },
            {
                path: '/blogs',
                element: LazyLoadRoute(Blog),
            },
             {
                path: '/news',
                element: LazyLoadRoute(OurNews),
            },
            {
                
                path: '/reports-policies',
                element: LazyLoadRoute(ReportsPolicies),
            },
            {
                path: ':itemType/:modalTitle',
                element: LazyLoadRoute(ReportsPolicies),
            },
            {
                path: '/gallery&media',
                element: LazyLoadRoute(GalleryMedia),
            },
        ],
    },
];

const router = createBrowserRouter(routes);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes — matches individual query overrides
      gcTime: 10 * 60 * 1000, // 10 minutes — keeps data in cache longer after unmount
      retry: (failureCount, error: any) => {
        if (error?.response?.status === 404) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
});

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
            {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
    );
}

export default App;